import { useId } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

import {
  APP_ACCESS_MODE_DESCRIPTIONS,
  APP_ACCESS_MODE_LABELS,
  APP_ACCESS_MODES,
} from './app-access-mode';
import { useAppExclusionsContext } from './use-app-exclusions';

import type { AppAccessMode } from '@electron/shared/types';

export const AppAccessModeSwitch = ({ className }: { className?: string }) => {
  const id = useId();
  const {
    appAccessMode,
    appAccessModeLockedByTeam,
    setAppAccessMode,
    savingAppAccessMode,
    appAccessModeError,
    loading,
    loadFailed,
  } = useAppExclusionsContext();

  return (
    <YStack className={cn('gap-2', className)} aria-busy={savingAppAccessMode}>
      <XStack className="flex-wrap items-center justify-between gap-4">
        <YStack className="min-w-0 flex-1 basis-48 gap-1">
          <Text id={`${id}-label`} size="sm">
            Which apps Littlebird sees
          </Text>
          <Text id={`${id}-description`} size="xs" variant="muted">
            {appAccessModeLockedByTeam
              ? 'Your team only allows the apps it has approved.'
              : APP_ACCESS_MODE_DESCRIPTIONS[appAccessMode]}
          </Text>
        </YStack>
        <DropdownMenuRoot>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              isLoading={savingAppAccessMode}
              disabled={
                loading ||
                loadFailed ||
                savingAppAccessMode ||
                appAccessModeLockedByTeam
              }
              className="shrink-0 gap-2"
              aria-labelledby={`${id}-label ${id}-value`}
              aria-describedby={`${id}-description`}
            >
              <span id={`${id}-value`}>
                {APP_ACCESS_MODE_LABELS[appAccessMode]}
              </span>
              <Icons.chevronDown className="size-3 text-foreground-muted" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuRadioGroup
              value={appAccessMode}
              onValueChange={(mode) => setAppAccessMode(mode as AppAccessMode)}
            >
              {APP_ACCESS_MODES.map((mode) => (
                <DropdownMenuRadioItem
                  key={mode}
                  value={mode}
                  className="items-start py-2 pr-8"
                >
                  <YStack className="gap-1">
                    <span className="text-sm">
                      {APP_ACCESS_MODE_LABELS[mode]}
                    </span>
                    <span className="text-xs text-foreground-muted">
                      {APP_ACCESS_MODE_DESCRIPTIONS[mode]}
                    </span>
                  </YStack>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenuRoot>
      </XStack>
      <span role="status" className="sr-only">
        {savingAppAccessMode ? 'Saving app access…' : ''}
      </span>
      {appAccessModeError && (
        <Text size="sm" role="alert" className="text-danger-9">
          {appAccessModeError}
        </Text>
      )}
    </YStack>
  );
};
